/* AdvisoryViewRenderer.js */

const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces");
const FileName = require("@gluenetworks/steve_utilities").fileName;
const ExposureFreshness = require("../exposure/ExposureFreshness");
const _ = require("lodash");
class AdvisoryViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("advisory-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;
        this._viewActionsAdded = false;
        this._fileNamer = FileName;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.pagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );
        this._registerGridListeners();
    }

    _registerGridListeners() {
        this._advisoryGrid.on("UserSorted", (this._onUserSorted = this._onUserSorted.bind(this)));
        this._advisoryGrid.on(
            "UserRowDoubleClicked",
            (this._openAdvisoryView = this._openAdvisoryView.bind(this))
        );
    }

    _unregisterListeners() {
        if (!_.isUndefined(this._model.pagingFeed)) {
            this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        }
        this._unregisterGridListeners();
    }

    _unregisterGridListeners() {
        if (!_.isUndefined(this._advisoryGrid)) {
            this._advisoryGrid.removeListener("UserSorted", this._onUserSorted);
            this._advisoryGrid.removeListener("UserRowDoubleClicked", this._openAdvisoryView);
        }
    }

    /**
     * Advisory-catalog freshness chip above the grid: newest successful
     * support-data sync vs the org advisoryStaleDays window, evaluated at
     * render time. Unknown (no successful sync recorded, prefs unreadable)
     * styles like stale — the chip never certifies freshness it cannot prove.
     * Idempotent: replaces any previous chip. Skipped when the grid never
     * mounted (default-message path).
     * @param {{advisorySyncAt: Number|null, advisoryStaleDays: Number|undefined}|null} freshness
     */
    _mountCatalogFreshness(freshness) {
        if (!this._advisoryGrid || !this._rootElem) {
            return;
        }
        this._removeCatalogFreshnessChip();
        const now = Date.now();
        const advisorySyncAt = freshness ? freshness.advisorySyncAt : null;
        const status = ExposureFreshness.ageStatus(
            advisorySyncAt,
            freshness ? freshness.advisoryStaleDays : undefined,
            now
        );

        const strip = document.createElement("advisory-freshness-gl");
        const caption = document.createElement("span");
        caption.className = "freshness-caption";
        caption.textContent = "Advisory catalog:";
        strip.appendChild(caption);

        const chip = document.createElement("advisory-freshness-chip");
        chip.setAttribute("for", status);
        const statusText = document.createElement("span");
        statusText.className = "freshness-chip-status";
        statusText.textContent =
            status === "unknown" ? "Unknown" : status === "stale" ? "Stale" : "Fresh";
        chip.appendChild(statusText);
        const age = document.createElement("span");
        age.className = "freshness-chip-age";
        age.textContent =
            typeof advisorySyncAt === "number"
                ? "synced " + ExposureFreshness.formatAge(advisorySyncAt, now)
                : "no successful sync recorded";
        chip.appendChild(age);
        if (typeof advisorySyncAt === "number") {
            chip.title =
                "Last successful support-data sync: " + new Date(advisorySyncAt).toLocaleString();
        } else {
            chip.title = "Freshness cannot be verified — treat as stale";
        }
        strip.appendChild(chip);

        this._rootElem.insertBefore(strip, this._rootElem.firstChild);
    }

    _removeCatalogFreshnessChip() {
        if (!this._rootElem) {
            return;
        }
        this._rootElem.querySelectorAll("advisory-freshness-gl").forEach((chip) => chip.remove());
    }

    async _showDefaultMessageDisplay() {
        try {
            const vendor = this._model._gameEditor._device.getDetectedRawVendor();
            const vendorList = [];
            let message = "";

            // Add Cisco if enabled
            if (this._model._gameEditor._isCiscoSupportEnabled) {
                vendorList.push("cisco");
            }

            // Add NIST-supported vendors if enabled
            if (this._model._gameEditor._isNistSupportEnabled) {
                try {
                    const vendorListMap = await this._model.retrieveVendorOsMap();
                    if (vendorListMap) {
                        vendorList.push(...Object.keys(vendorListMap));
                    }
                } catch (err) {
                    this._logger.warn(
                        {error: err},
                        "AdvisoryViewRenderer._showDefaultMessageDisplay error"
                    );
                    message = "Unable to get supported NIST vendor list";
                }
            }

            message =
                message ||
                (vendor && vendorList.includes(vendor)
                    ? "No advisories available"
                    : "NIST NVD advisories are not yet supported for this vendor and OS");

            await this._model.gameEditor.showDefaultMessageView({message});
            this._defaultViewActive = true;
        } catch (err) {
            this._logger.warn(
                {error: err},
                "AdvisoryViewRenderer._showDefaultMessageDisplay error"
            );
        }
    }

    _hideDefaultMessageDisplay() {
        return this._model.gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "AdvisoryViewRenderer._hideDefaultMessageDisplay error"
                );
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        return Promise.resolve()
            .then(() => {
                if (_.isUndefined(this._advisoryGrid)) {
                    return Promise.resolve();
                } else {
                    return this._advisoryGrid.shutdown();
                }
            })
            .then(
                () => {
                    this._model = undefined;
                    this._rootElem.remove();
                    this._rootElem = undefined;
                    this._defaultViewActive = undefined;
                    this._advisoryGrid = undefined;
                    this._gridRows = undefined;
                },
                (err) => {
                    this.model._logger.warn({error: err}, "AdvisoryViewRenderer.shutdown() error.");
                }
            );
    }

    render() {
        return this._model
            .whenModelIsReady()
            .then(() => {
                this._advisoryGrid = new EntityGrid({
                    gridKey: this._model._gridKey,
                    columns: this._model.columnConfig,
                    pagingFeed: this._model.pagingFeed,
                    showHeader: true,
                });

                if (!this._rootElem.hasChildNodes()) {
                    this._rootElem.appendChild(this._advisoryGrid.renderer.rootElement);
                }
                this._registerListeners();
            })
            .then(() => this._addViewActions())
            .then(() => {
                // Mounts the freshness chip too — every later feed refresh takes the
                // same path, so first render and refresh cannot drift apart.
                this._onFeedRefreshView();
            })
            .catch((err) => {
                this._logger.warn({info: err}, "AdvisoryViewRenderer.render() warning");
                this._showDefaultMessageDisplay();
            });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }
    _addViewActions() {
        if (!this._viewActionsAdded) {
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Download",
                name: "DownloadAdvisory",
                handler: (this._onDownloadAdvisorySummary =
                    this._onDownloadAdvisorySummary.bind(this)),
            });
            this._viewActionsAdded = true;
        }
    }
    _removeViewActions() {
        if (this._viewActionsAdded) {
            this.model.gameEditor.clearViewActions();
        }
    }
    async _onDownloadAdvisorySummary() {
        this.model.gameEditor.disableViewAction("DownloadAdvisory");
        const deviceDetials = this.model.gameEditor.getDevice(),
            advisoryData = _.values(await this._model.getAdvisoryList()),
            fileName = this._fileNamer({
                solution: "DM",
                general: "AdvisorySummary",
                specific: deviceDetials.name,
                suffix: "csv",
            });
        //fileName =  "AdvisorySummary-" + deviceDetials.name + "-" + new Date(Date.now()).toISOString();
        const printdata = [];
        advisoryData.forEach((advisory) => {
            const advData = advisory.getPristineProperties();
            const extobj = {};
            extobj.Criticality = advData.data.sir;
            extobj["Advisory ID"] = advData.data.advisoryId;
            extobj.FirstFound = this._toLocaleString(advData.data.firstPublished);
            extobj.Title = advData.data.advisoryTitle;
            extobj.FirstFixed = advData.data.firstFixed;
            extobj["CVE ID"] = advData.data.cves.join("; ");
            printdata.push(extobj);
        });
        this.model.gameEditor.saveCSVFile(fileName, this.model.gameEditor.convertToCSV(printdata));
        this.model.gameEditor.enableViewAction("DownloadAdvisory");
        return this;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onUserSorted(eventData) {
        this._model._onUserSorted(eventData);
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;
        if (totalEntities === 0) {
            if (!this._defaultViewActive) {
                this._showDefaultMessageDisplay();
            }
            this._hideViewActions();
        } else {
            if (this._defaultViewActive) {
                this._hideDefaultMessageDisplay();
            }
        }
        // The feed reloading means the advisory data on screen just changed, which is
        // exactly when a claim about how fresh that data is can go stale. Re-read rather
        // than leave the chip asserting the age it had at first render.
        this._refreshCatalogFreshness();
    }

    /**
     * Re-reads the catalog freshness and remounts the chip. Best-effort: neither call
     * rejects, and unlicensed orgs just clear the chip instead of reading data that can
     * never populate there.
     * @returns {Promise}
     */
    _refreshCatalogFreshness() {
        return this._model
            .isCatalogFreshnessLicensed()
            .then((licensed) => {
                if (!licensed) {
                    this._removeCatalogFreshnessChip();
                    return;
                }
                return this._model
                    .loadCatalogFreshness()
                    .then((freshness) => this._mountCatalogFreshness(freshness));
            })
            .catch((err) => {
                this._logger.warn(
                    {info: err},
                    "AdvisoryViewRenderer._refreshCatalogFreshness() warning"
                );
            });
    }

    _openAdvisoryView(row) {
        const advisoryEntity = this._model.pagingFeed.getEntityById(row.selectedRow);
        if (advisoryEntity) {
            this._model._gameEditor.setActiveView(
                Registrations.DIE_VIEW_ADVISORY_DETAILS,
                [
                    {
                        value: {
                            fqn: Registrations.DIE_VIEW_ADVISORY_SUMMARY,
                        },
                        content: "Advisory Summary",
                        left: {
                            arrow: {
                                direction: "left",
                            },
                        },
                    },
                ],
                {
                    advisoryId: advisoryEntity.id,
                }
            );
        } else {
            this._model._logger.warn(
                {},
                "User clicked on viewMore in the activityView for a row that does not have a Advisory Entity."
            );
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    _toLocaleString(val) {
        const d = new Date(val);
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } else {
            return "";
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = AdvisoryViewRenderer;
